import { cx } from '@/lib/cx';
import styles from './RouteLine.module.css';

export interface RouteLineProps {
  /** Numero de destellos repartidos sobre la ruta. */
  sparkles?: number;
  /** Anima el trazado al entrar en pantalla (lo controla DrawSvgLine). */
  animated?: boolean;
  className?: string;
}

interface Nodo {
  readonly x: number;
  readonly y: number;
}

/**
 * LOS VERTICES DE LA ONDA. Cada destello se sienta en uno.
 *
 * Van a la altura de los CENTROS DE TARJETA (10 / 30 / 50 / 70 / 90 % del ancho)
 * porque es donde el poster los pone: en los dobleces de la linea, uno por
 * destino, no repartidos por longitud de trazo.
 *
 * La amplitud (y 14..78 de un viewBox de 96) sale de MEDIR el poster, no de
 * tantear: la linea dorada de `DEMO_0_Hub.png` recorre y 700..752, o sea **52 px
 * de onda** sobre 1024 de ancho. El trazo anterior recorria 16 px reales y se
 * leia como una recta; estirarlo por CSS lo llevo a 21 px y ahi se agoto, porque
 * el techo no lo pone la altura sino la ENVOLVENTE de la Bezier.
 */
const NODOS: readonly Nodo[] = [
  { x: 100, y: 16 },
  { x: 300, y: 78 },
  { x: 500, y: 20 },
  { x: 700, y: 76 },
  { x: 900, y: 14 },
];

/** Entrada y salida del trazo, para que la onda no empiece cortada en seco. */
const ENTRADA: Nodo = { x: 0, y: 54 };
const SALIDA: Nodo = { x: 1000, y: 26 };

/**
 * Une los puntos con Beziers de TANGENTE HORIZONTAL en los dos extremos.
 *
 * Es la clave de la amplitud, y el motivo por el que el `path` anterior no podia
 * dar la onda del poster por mucho que se estirase el `svg`: una Bezier cubica se
 * queda DENTRO de su envolvente convexa, asi que con puntos de control oblicuos
 * (`C 180 12, 320 88, 500 46`) la curva jamas alcanza el 12 ni el 88 de sus
 * controles — recorria y 34..58 y nada mas. Con la tangente horizontal el vertice
 * ES el extremo de la curva, y la amplitud pedida es la amplitud real.
 */
function suave(inicio: Nodo, resto: readonly Nodo[]): string {
  let d = `M${inicio.x} ${inicio.y}`;
  let a = inicio;
  for (const p of resto) {
    const c = (p.x - a.x) * 0.45;
    d += ` C${a.x + c} ${a.y}, ${p.x - c} ${p.y}, ${p.x} ${p.y}`;
    a = p;
  }
  return d;
}

const PATH = suave(ENTRADA, [...NODOS, SALIDA]);

/**
 * Destello de 4 puntas, ALARGADO EN VERTICAL como los del poster (ahi miden
 * ~18x45 px: las puntas verticales son 2,5 veces las horizontales).
 *
 * Ojo con el tamano: el `svg` va con `preserveAspectRatio="none"`, asi que las
 * escalas de x y de y no son la misma (0,93 y 0,85 a 1024) y la estrella sale un
 * 9 % achatada. A este tamano no se ve, y el radio se elige contra el resultado
 * MEDIDO en pantalla, no contra las unidades del viewBox.
 *
 * Se dibuja como una figura propia y no como un tramo gordo del trazo: un tramo
 * de `stroke` con `linecap: round` da un PUNTO REDONDO, que es lo que habia.
 */
function estrella(cx: number, cy: number, rx: number, ry: number): string {
  const kx = rx * 0.16;
  const ky = ry * 0.16;
  return [
    `M${cx} ${cy - ry}`,
    `Q${cx + kx} ${cy - ky} ${cx + rx} ${cy}`,
    `Q${cx + kx} ${cy + ky} ${cx} ${cy + ry}`,
    `Q${cx - kx} ${cy + ky} ${cx - rx} ${cy}`,
    `Q${cx - kx} ${cy - ky} ${cx} ${cy - ry}`,
    'Z',
  ].join(' ');
}

/**
 * "La Ruta del Despertar": la linea discontinua dorada que une los 5 destinos
 * en el hub. SVG propio, fluido, con destellos en los vertices de la onda.
 *
 * OJO CON `pathLength={100}`: normaliza la longitud del trazo a 100, asi que un
 * `stroke-dasharray` sobre este `path` esta en **por ciento de la ruta**, no en
 * unidades del viewBox. Un `2.4 4.2` no son 2,4 px de guion: son el 2,4 % de
 * ~1060 px, o sea guiones de 25 px con huecos de 44 — justo lo contrario del
 * punteado fino del poster (~4 px de punto cada 6). La cadencia buena vive en
 * `RouteLine.module.css` y va en decimas.
 */
export function RouteLine({ sparkles = 5, animated = false, className }: RouteLineProps) {
  const nodos = NODOS.slice(0, Math.max(0, Math.min(sparkles, NODOS.length)));

  return (
    <svg
      className={cx(styles.route, animated && styles.animated, className)}
      viewBox="0 0 1000 96"
      preserveAspectRatio="none"
      aria-hidden="true"
      focusable="false"
    >
      <path className={styles.path} d={PATH} pathLength={100} />
      {nodos.map((n) => (
        <path key={`${n.x}-${n.y}`} className={styles.mark} d={estrella(n.x, n.y, 7.5, 20)} />
      ))}
    </svg>
  );
}
